import { getBackendUrl } from "../config/apollo-client";

// Types for file upload
export interface FileUploadResponse {
    success: boolean;
    message: string;
    filename?: string;
    url?: string;
    iconUrl?: string
    labelId?: number
}

export interface FileUploadProgress {
    loaded: number;
    total: number;
    percentage: number;
}

// FileUpload service class
export class FileUploadService {
    private static instance: FileUploadService;
    private getBaseUrl = () => getBackendUrl();

    // Supported file types for validation - webp, mp3, mp4, gif
    private supportedTypes = {
        'image/png': ['.png'],
        'image/jpg': ['.jpg'],
        'image/jpeg': ['.jpeg'],
        'image/webp': ['.webp'],
        'image/gif': ['.gif'],
        'video/mp4': ['.mp4'],
        'video/webm': ['.webm'],
        'application/json': ['.json'],
        'audio/mpeg': ['.mp3'],
        'audio/mp3': ['.mp3']
    };

    private constructor() { }

    public static getInstance(): FileUploadService {
        if (!FileUploadService.instance) {
            FileUploadService.instance = new FileUploadService();
        }
        return FileUploadService.instance;
    }

    // Validate file before upload
    validateFile(file: File): { isValid: boolean; error?: string } {
        // Check file extension
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const isValidExtension = Object.values(this.supportedTypes)
            .flat()
            .includes(fileExtension);

        if (!isValidExtension) {
            return {
                isValid: false,
                error: `Unsupported file type. Supported types: .png, .jpg, .jpeg, .webp, .gif, .mp4, .webm, .json, .mp3 only`
            };
        }

        return { isValid: true };
    }

    // Upload single file to server
    async uploadFile(
        file: File,
        assetName: string,
        onProgress?: (progress: FileUploadProgress) => void
    ): Promise<FileUploadResponse> {
        try {
            // Validate file
            const validation = this.validateFile(file);
            if (!validation.isValid) {
                return {
                    success: false,
                    message: validation.error || 'Invalid file'
                };
            }

            // Get auth token
            const token = localStorage.getItem('authToken');

            if (!token) {
                return {
                    success: false,
                    message: 'Authentication required'
                };
            }

            // Log for debugging
            console.log('Uploading file:', {
                name: file.name,
                type: file.type,
                size: file.size,
                assetName: assetName
            });

            // Get file content type
            const contentType = file.type || this.getContentTypeFromExtension(file.name);

            // Create XMLHttpRequest for progress tracking
            return new Promise<FileUploadResponse>((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                // Track upload progress
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable && onProgress) {
                        const progress: FileUploadProgress = {
                            loaded: event.loaded,
                            total: event.total,
                            percentage: Math.round((event.loaded / event.total) * 100)
                        };
                        onProgress(progress);
                    }
                });

                // Handle completion
                xhr.addEventListener('load', () => {
                    console.log('Upload response status:', xhr.status);
                    console.log('Upload response text:', xhr.responseText);

                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve({
                                success: true,
                                message: response.message || 'File uploaded successfully',
                                filename: response.filename,
                                url: response.url
                            });
                        } else {
                            resolve({
                                success: false,
                                message: response.message || `Upload failed with status ${xhr.status}`
                            });
                        }
                    } catch (error) {
                        resolve({
                            success: false,
                            message: `Failed to parse server response: ${xhr.responseText}`
                        });
                    }
                });

                // Handle errors
                xhr.addEventListener('error', () => {
                    resolve({
                        success: false,
                        message: 'Network error during upload'
                    });
                });

                // Handle timeout
                xhr.addEventListener('timeout', () => {
                    resolve({
                        success: false,
                        message: 'Upload timeout'
                    });
                });

                // Configure and send request with filename parameter
                const uploadUrl = `${this.getBaseUrl()}/assets/upload?filename=${encodeURIComponent(assetName)}`;
                console.log('Upload URL:', uploadUrl);
                console.log('Content-Type:', contentType);

                xhr.open('POST', uploadUrl);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.setRequestHeader('Content-Type', contentType);
                xhr.timeout = 300000; // 5 minutes timeout
                xhr.send(file); // Send file directly as binary data
            });

        } catch (error) {
            console.error('Upload error:', error);
            return {
                success: false,
                message: 'Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error')
            };
        }
    }

    // Upload game asset (icon) to server
    async uploadGameAsset(
        file: File,
        providerId: string | number,
        gameId: string,
        onProgress?: (progress: FileUploadProgress) => void,
        labelId?: number
    ): Promise<FileUploadResponse> {
        try {
            // Validate file
            const validation = this.validateFile(file);
            if (!validation.isValid) {
                return {
                    success: false,
                    message: validation.error || 'Invalid file'
                };
            }

            // Get auth token
            const token = localStorage.getItem('authToken');

            if (!token) {
                return {
                    success: false,
                    message: 'Authentication required'
                };
            }

            // Log for debugging
            console.log('Uploading game asset:', {
                name: file.name,
                type: file.type,
                size: file.size,
                providerId,
                gameId,
                labelId
            });

            // Get file content type
            const contentType = file.type || this.getContentTypeFromExtension(file.name);

            // Create XMLHttpRequest for progress tracking
            return new Promise<FileUploadResponse>((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                // Track upload progress
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable && onProgress) {
                        const progress: FileUploadProgress = {
                            loaded: event.loaded,
                            total: event.total,
                            percentage: Math.round((event.loaded / event.total) * 100)
                        };
                        onProgress(progress);
                    }
                });

                // Handle completion
                xhr.addEventListener('load', () => {
                    console.log('Game asset upload response status:', xhr.status);
                    console.log('Game asset upload response text:', xhr.responseText);

                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve( response);
                        } else {
                            resolve({
                                success: false,
                                message: response.message || `Upload failed with status ${xhr.status}`
                            });
                        }
                    } catch (error) {
                        resolve({
                            success: false,
                            message: `Failed to parse server response: ${xhr.responseText}`
                        });
                    }
                });

                // Handle errors
                xhr.addEventListener('error', () => {
                    resolve({
                        success: false,
                        message: 'Network error during upload'
                    });
                });

                // Handle timeout
                xhr.addEventListener('timeout', () => {
                    resolve({
                        success: false,
                        message: 'Upload timeout'
                    });
                });

                // Configure and send request with providerId and gameId parameters
                // With labelId the icon overrides the game's icon for that label only
                const labelQuery = labelId !== undefined ? `&labelId=${encodeURIComponent(labelId)}` : '';
                const uploadUrl = `${this.getBaseUrl()}/assets/uploadGameAsset?providerId=${encodeURIComponent(providerId)}&gameId=${encodeURIComponent(gameId)}${labelQuery}`;
                xhr.open('POST', uploadUrl);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.setRequestHeader('Content-Type', contentType);
                xhr.timeout = 300000; // 5 minutes timeout                
                xhr.send(file); // Send file directly as binary data

            });

        } catch (error) {
            console.log('Game asset upload error:', error);
            return {
                success: false,
                message: 'Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error')
            };
        }
    }

    // Helper method to get file type icon
    getFileTypeIcon(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'webp':
                return '🖼️';
            case 'gif':
                return '🖼️';
            case 'mp4':
                return '🎥';
            case 'mp3':
                return '🎵';
            default:
                return '📁';
        }
    }

    // Helper method to format file size
    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    // Helper method to check if file type is supported
    isFileTypeSupported(filename: string): boolean {
        const extension = '.' + filename.split('.').pop()?.toLowerCase();
        return Object.values(this.supportedTypes).flat().includes(extension);
    }

    // Helper method to get content type from file extension
    private getContentTypeFromExtension(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'webp':
                return 'image/webp';
            case 'gif':
                return 'image/gif';
            case 'png':
                return 'image/png';
            case 'jpg':
            case 'jpeg':
                return 'image/jpeg';
            case 'mp4':
                return 'video/mp4';
            case 'webm':
                return 'video/webm';
            case 'json':
                return 'application/json';
            case 'mp3':
                return 'audio/mpeg';
            default:
                return 'application/octet-stream';
        }
    }
}

// Export singleton instance
export const fileUploadService = FileUploadService.getInstance();

// Export convenience function
export async function uploadFile(
    file: File,
    assetName: string,
    onProgress?: (progress: FileUploadProgress) => void
): Promise<FileUploadResponse> {
    return fileUploadService.uploadFile(file, assetName, onProgress);
}

// Export convenience function for game asset upload
export async function uploadGameAsset(
    file: File,
    providerId: string | number,
    gameId: string,
    onProgress?: (progress: FileUploadProgress) => void,
    labelId?: number
): Promise<FileUploadResponse> {
    return fileUploadService.uploadGameAsset(file, providerId, gameId, onProgress, labelId);
}